/* Aria Robotics — Scroll-gesteuerter Video-Hero (Frame-Scrubbing auf Canvas).
 * Ported aus scroll-hero.jsx. */
import * as React from 'react';
import { Container, Btn, Chip, Icon, go, res } from '@/ui';

const N = 48;
const FRAMES = Array.from({ length: N }, (_, i) => `assets/hero-frames/f${String(i).padStart(3, '0')}.jpg`);

const SCRUB_END = 0.66;
const TEXT_START = 0.68;
const TEXT_FULL = 0.82;

function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, cw: number, ch: number): void {
  const ir = img.width / img.height;
  const cr = cw / ch;
  let dw: number;
  let dh: number;
  if (cr > ir) { dw = cw; dh = cw / ir; } else { dh = ch; dw = ch * ir; }
  ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
}

export function ScrollHero() {
  const trackRef = React.useRef<HTMLElement | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const scrimRef = React.useRef<HTMLDivElement | null>(null);
  const textRef = React.useRef<HTMLDivElement | null>(null);
  const hintRef = React.useRef<HTMLDivElement | null>(null);
  const imgsRef = React.useRef<HTMLImageElement[]>([]);
  const lastFi = React.useRef<number>(-1);
  const [ready, setReady] = React.useState(false);

  const draw = React.useCallback((fi: number): void => {
    const canvas = canvasRef.current;
    const imgs = imgsRef.current;
    const img = imgs[fi];
    if (!canvas || !img || !img.complete || !img.naturalWidth) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawCover(ctx, img, canvas.width, canvas.height);
    lastFi.current = fi;
  }, []);

  const resize = React.useCallback((): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    draw(lastFi.current >= 0 ? lastFi.current : 0);
  }, [draw]);

  // Preload frames
  React.useEffect(() => {
    let alive = true;
    let loaded = 0;
    const imgs = FRAMES.map((src) => {
      const im = new Image();
      im.decoding = 'async';
      im.onload = () => {
        loaded++;
        if (alive && lastFi.current < 0) { resize(); draw(0); }
        if (loaded === N && alive) { setReady(true); draw(lastFi.current >= 0 ? lastFi.current : 0); }
      };
      im.src = res(src);
      return im;
    });
    imgsRef.current = imgs;
    return () => { alive = false; };
  }, [draw, resize]);

  React.useEffect(() => {
    let raf = 0;
    const update = (): void => {
      raf = 0;
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const total = track.offsetHeight - window.innerHeight;
      const p = Math.min(Math.max(-rect.top / (total || 1), 0), 1);

      const fi = Math.round(Math.min(p / SCRUB_END, 1) * (N - 1));
      if (fi !== lastFi.current) draw(fi);

      const tp = Math.min(Math.max((p - TEXT_START) / (TEXT_FULL - TEXT_START), 0), 1);
      if (scrimRef.current) scrimRef.current.style.opacity = String(tp * 0.9);
      if (textRef.current) {
        textRef.current.style.opacity = String(tp);
        textRef.current.style.transform = `translateY(${(1 - tp) * 26}px)`;
        textRef.current.style.pointerEvents = tp > 0.5 ? 'auto' : 'none';
      }
      if (hintRef.current) hintRef.current.style.opacity = String(Math.max(1 - p / 0.03, 0));
    };
    const onScroll = (): void => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resize);
    resize();
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', resize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [draw, resize]);

  return (
    <section ref={trackRef} style={{ position: 'relative', height: '340vh', background: '#e9eaec' }}>
      <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', background: '#e9eaec' }}>
        <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />

        {!ready && (
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 40, display: 'flex', justifyContent: 'center' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(14,27,46,0.5)' }}>Nestor lädt…</span>
          </div>
        )}

        <div ref={scrimRef} style={{ position: 'absolute', inset: 0, opacity: 0, pointerEvents: 'none', background: 'radial-gradient(120% 90% at 50% 40%, rgba(14,27,46,0.30) 0%, rgba(14,27,46,0.60) 55%, rgba(14,27,46,0.86) 100%)' }} />

        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end' }}>
          <Container style={{ position: 'relative', zIndex: 2, paddingBottom: 'clamp(48px, 9vh, 110px)' }}>
            <div ref={textRef} style={{ opacity: 0, transform: 'translateY(26px)', transition: 'none', maxWidth: 720, color: '#fff' }}>
              <Chip tone="inverse" dot>Jetzt vorbestellbar · Nestor 01</Chip>
              <h1 style={{ fontSize: 'clamp(38px, 6vw, 72px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.0, margin: '20px 0 0', textShadow: '0 2px 30px rgba(0,0,0,0.45)' }}>
                Dein Zuhause,<br /><span style={{ color: 'var(--primary-100)' }}>in guten Händen.</span>
              </h1>
              <p style={{ fontSize: 20, fontWeight: 300, color: 'rgba(255,255,255,0.9)', marginTop: 18, maxWidth: '46ch', lineHeight: 1.55, textShadow: '0 1px 14px rgba(0,0,0,0.5)' }}>
                Nestor 01 erwacht zum Leben — und übernimmt die tägliche Hausarbeit. Ehrlich, ruhig und immer nur, wenn Sie es möchten.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 30 }}>
                <Btn variant="cta" size="lg" iconRight="arrow-right" onClick={() => go('/kontakt')}>Nestor 01 vorbestellen</Btn>
                <Btn variant="ghostInverse" size="lg" iconLeft="play-circle" onClick={() => go('/nestor-01')}>Nestor entdecken</Btn>
              </div>
            </div>
          </Container>
        </div>

        <div ref={hintRef} style={{ position: 'absolute', left: 0, right: 0, bottom: 26, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: 'rgba(14,27,46,0.55)', pointerEvents: 'none' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, letterSpacing: '0.16em', textTransform: 'uppercase' }}>Scrollen</span>
          <span className="ar-hero-bounce" style={{ display: 'inline-flex' }}><Icon name="chevron-down" size={22} /></span>
        </div>
      </div>
    </section>
  );
}
